import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { get } from 'lodash/fp';

import { autosize } from '../../../../utils';
import Question from '../Question';

const S = {
  /* eslint-disable object-curly-newline */
  TextAreaWrapper: styled.div.attrs(({ field }) => ({
    style: {
      top: `${field.y}%`,
      left: `${field.x}%`,
      width: `${field.width}%`,
    },
  }))`
    position: absolute;
    z-index: 100;
    ${({ error, focused, large, theme }) => !focused && css`
      ::after {
        content: '';
        position: absolute;
        right: 2px;
        bottom: 2px;
        border: ${`${large ? 4 : 3}px solid ${error ? theme.palette.status.error.main : theme.palette.primary.light}`};
        border-top-color: transparent;
        border-left-color: transparent;
      }
    `}
  `,
  /* eslint-enable */
  TextArea: styled.textarea`
    display: flex;
    align-items: center;
    width: 100%;
    height: 16px;
    min-height: 16px;
    max-height: 16px;
    padding: 2px 2px 2px 8px;
    display: flex;
    align-items: center;
    border-style: none;
    color: ${({ theme }) => theme.palette.grey[900]};
    background-color: ${({ error, theme }) => (error ? theme.palette.status.error.background : '#e5e7f0')};
    font-size: ${({ large, theme }) => theme.typography.pxToRem(large ? 13 : 10)};
    font-weight: 700;
    line-height: 1.54;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.24);
    outline: none;
    resize: none;
    ::placeholder {
      color: ${({ error, theme }) => (error ? theme.palette.status.error.main : theme.palette.primary.light)};
      font-weight: 700;
    }
    :hover {
      padding: 1px 1px 1px 7px;
      border: 1px solid ${({ error, theme }) => (error ? theme.palette.status.error.main : theme.palette.primary.light)};
    }
    :focus {
      max-height: none;
      padding: 0 0 0 6px;
      border: 2px solid ${({ error, theme }) => (error ? theme.palette.status.error.main : theme.palette.primary.light)};
      ::placeholder {
        color: ${({ error, theme }) => (error ? theme.palette.status.error.main : 'rgba(93, 108, 164, 0.38)')};
    }
    }
  `,
  FieldText: styled.p`
    flex-grow: 1;
    overflow: hidden;
    max-width: 95%;
    text-overflow: ellipsis;
    white-space: normal;
  `,
};

const DescriptiveField = ({
  field: { onBlur, onChange, ...field }, form, large, question,
}) => {
  const [focused, setFocused] = useState(false);

  const error = useMemo(() => !!(
    get(field.name, form.errors) && get(field.name, form.touched)
  ), [field.name, form.errors, form.touched]);

  const textareaRef = useRef(null);

  const handleFocus = useCallback(() => setFocused(true), []);
  const handleBlur = useCallback((event) => {
    onBlur(event);

    setFocused(false);
  }, [onBlur]);
  const handleChange = useCallback((event) => {
    onChange(event);
  }, [onChange]);

  useEffect(() => {
    autosize(textareaRef.current);
  }, []);

  return (
    <div>
      <Question error={error} large={large} question={question} />
      <S.TextAreaWrapper
        error={error}
        focused={focused}
        field={get('x', question.field)
          ? question.field
          : { x: question.field.offsetX, y: question.field.offsetY, width: 20 }}
      >
        <S.TextArea
          ref={textareaRef}
          error={error}
          large={large}
          placeholder={`${question.number}번 질문 답변`}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          {...field}
        />
      </S.TextAreaWrapper>
    </div>
  );
};

DescriptiveField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    errors: PropTypes.shape({}).isRequired,
    touched: PropTypes.shape({}).isRequired,
  }).isRequired,
  large: PropTypes.bool,
  question: PropTypes.shape({
    number: PropTypes.number,
    field: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number,
      offsetX: PropTypes.number,
      offsetY: PropTypes.number,
    }),
    type: PropTypes.oneOf(['SINGLE_SELECTION', 'MULTIPLE_SELECTION', 'DESCRIPTIVE']),
  }).isRequired,
};

DescriptiveField.defaultProps = {
  large: false,
};

export default DescriptiveField;
